import React, { useMemo } from 'react';
import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import {
  apiV2EnvMonitorDashboardOverallTrendPost,
  type V2EnvMonitorDashboardOverallTrendPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import titleIcon from '@/assets/images/title_icon.png';
import { keepTwoAndWanOrLine, keepTwoOrNull } from '@/utils/utils';
import { TrendGranularity } from '@/pages/EnvMonitorKanban/types';
import { timeFormat } from '@/pages/EnvMonitorKanban/components/OverallEmissionChart';
import styles from './index.module.scss';

export type MonitorItem = {
  label: string;
  propertyId: number;
};

const getTrendRange = (dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null, granularity: TrendGranularity) => {
  if (dateRange?.[0] && dateRange?.[1]) {
    const isMonth = granularity === TrendGranularity.MONTH;
    return {
      startTime: (isMonth ? dateRange[0].startOf('month') : dateRange[0].startOf('day')).valueOf(),
      endTime: (isMonth ? dateRange[1].endOf('month') : dateRange[1].endOf('day')).valueOf(),
    };
  }
  const now = dayjs();
  return {
    startTime: now.startOf('month').valueOf(),
    endTime: now.endOf('day').valueOf(),
  };
};

const getChartOption = (
  trendData: V2EnvMonitorDashboardOverallTrendPostResponse | undefined,
  color: string,
  name: string,
  unit?: string,
  granularity?: TrendGranularity
) => {
  const chartData = trendData?.chartData ?? [];
  const xAxisFormat = granularity ? timeFormat(granularity) : 'MM-DD';
  const displayUnit = (unit ?? '').trim();
  const tooltipUnit = displayUnit?.toLowerCase() === 'ph' ? '' : displayUnit;

  return {
    backgroundColor: 'transparent',
    color: [color],
    grid: {
      top: 42,
      left: 16,
      right: 18,
      bottom: 16,
      containLabel: true,
    },
    legend: {
      right: 0,
      top: 0,
      itemWidth: 12,
      itemHeight: 12,
      icon: 'rect',
      data: [name],
    },
    tooltip: {
      show: true,
      trigger: 'axis',
      confine: true,
      formatter: (params: any) => {
        const list = Array.isArray(params) ? params : [params];
        const title = list[0]?.axisValue ?? '';
        const lines = list.map((item: any) => {
          const value = keepTwoAndWanOrLine(item.value);
          return `${item.marker ?? ''}${item.seriesName ?? ''}: ${value}${tooltipUnit ?? ''}`;
        });
        return [title, ...lines].filter(Boolean).join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: chartData.map(item => (item.time ? dayjs(item.time, 'x').format(xAxisFormat) : '--')),
    },
    yAxis: {
      type: 'value',
      name: `${name}(${displayUnit || ''})`,
      nameTextStyle: {
        padding: [0, 0, 0, 30],
      },
    },
    series: [
      {
        name,
        type: 'bar',
        data: chartData.map(item => keepTwoOrNull(item.value)),
        barMaxWidth: 24,
        itemStyle: {
          color,
        },
      },
    ],
  };
};

interface MonitorBlockProps {
  title: string;
  items: MonitorItem[];
  activeId: number;
  color: string;
  chartHeight: number;
  deviceType: number;
  granularity: TrendGranularity;
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
  extra?: React.ReactNode;
  onChange: (propertyId: number) => void;
}

const MonitorBlock: React.FC<MonitorBlockProps> = ({
  title,
  items,
  activeId,
  color,
  deviceType,
  granularity,
  dateRange,
  chartHeight,
  extra,
  onChange,
}) => {
  const theme = useTheme();
  const activeItem = useMemo(() => items.find(item => item.propertyId === activeId) ?? items[0], [activeId, items]);
  const { startTime, endTime } = useMemo(() => getTrendRange(dateRange, granularity), [dateRange, granularity]);
  const requestKey = useMemo(
    () => [deviceType, activeId, granularity, startTime, endTime].join('-'),
    [activeId, deviceType, endTime, granularity, startTime]
  );

  const { data: requestData } = useRequest(
    async () => {
      const response = await apiV2EnvMonitorDashboardOverallTrendPost({
        deviceType,
        propertyId: activeId,
        granularity: granularity as string,
        startTime,
        endTime,
      });
      return {
        key: requestKey,
        response,
      };
    },
    {
      refreshDeps: [requestKey],
    }
  );
  const data = requestData?.key === requestKey ? requestData.response : undefined;

  const value = data?.summaryValue;
  const unit = data?.unit;
  const chartOption = useMemo(
    () => getChartOption(data, color, activeItem.label, unit, granularity),
    [activeItem.label, color, data, unit, granularity]
  );

  return (
    <section className={styles.block}>
      <div className={styles.title}>
        <img src={titleIcon} alt="" />
        <span>{title}</span>
        {extra && <div className={styles.titleExtra}>{extra}</div>}
      </div>
      <div className={styles.tabs}>
        {items.map(item => (
          <button
            key={item.propertyId}
            type="button"
            className={`${styles.tab} ${item.propertyId === activeItem.propertyId ? styles.activeTab : ''}`}
            onClick={() => onChange(item.propertyId)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className={styles.summary}>
        <span>{activeItem.label}：</span>
        <strong style={{ color }}>
          {keepTwoAndWanOrLine(value)}
          {unit}
        </strong>
      </div>
      <div className={styles.chart}>
        <EchartsWithTheme
          key={requestKey}
          type="bar"
          style={{ height: chartHeight, width: '100%' }}
          option={chartOption}
          theme={theme}
        />
      </div>
    </section>
  );
};

export default MonitorBlock;
